import "./index.scss";
import "@fontsource/roboto/latin-300.css";
import "@fontsource/roboto/latin-400.css";
import "@fontsource/roboto/latin-500.css";
import "@fontsource/roboto/latin-700.css";
import "./theme/globalStyles.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import { createRef } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import { Collapse, CssBaseline, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import App from "./App";
import { validateFrontendEnv } from "./config/env";
import { persistor, store } from "./Redux/createStore";
import keycloak, {
  clearKeycloakCallbackParamsFromUrl,
  clearStoredKeycloakTokens,
  initKeycloak,
  saveStoredKeycloakTokens,
} from "./keycloak";
import { getProfile } from "./Redux/Actions";
import { LOGOUT } from "./Redux/ActionTypes";
import { LOGOUT_IN_PROGRESS_KEY } from "./Redux/Actions";
import { refreshToken } from "./Shared/baseURL";
import { snackBarPlacement, theme } from "./theme/themeConfig";
import { startOptionalServices } from "Utils/optionalServices";
import { startNewRelicBrowser } from "Utils/newRelicBrowser";

validateFrontendEnv();
startNewRelicBrowser();

const rootApp = document.getElementById("root");
const root = createRoot(rootApp);

clearKeycloakCallbackParamsFromUrl({ onlyIfMissingState: true });

const notistackRef = createRef();
const dismissNotification = (key) => {
  if (notistackRef.current) {
    notistackRef.current.closeSnackbar(key);
  }
};

const queryClient = new QueryClient();

const ReactApp = (
  <HelmetProvider>
	  <Provider store={store}>
		<PersistGate persistor={persistor}>
			<QueryClientProvider client={queryClient}>
			<BrowserRouter
				future={{
				v7_relativeSplatPath: true,
				v7_startTransition: true,
				}}
			>
				<StyledEngineProvider injectFirst>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<SnackbarProvider
					ref={notistackRef}
					anchorOrigin={snackBarPlacement}
					autoHideDuration={3000}
					variant="success"
					preventDuplicate
					maxSnack={1}
					TransitionComponent={Collapse}
					action={(key) => (
						<IconButton
						onClick={() => dismissNotification(key)}
						sx={{ color: "white" }}
						size="small"
						>
						<Close fontSize="small" />
						</IconButton>
					)}
					>
					<App />
					</SnackbarProvider>
				</ThemeProvider>
				</StyledEngineProvider>
			</BrowserRouter>
			</QueryClientProvider>
		</PersistGate>
	  </Provider>
  </HelmetProvider>
);

const renderReactApp = () => {
  startOptionalServices();
  root.render(ReactApp);
};

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const shouldRetryProfileFetch = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return false;
  }
  return status === 404 || status === 500 || !status;
};

const fetchProfileWithRetry = async () => {
  const backoffMs = [250, 400, 600, 800, 1200, 1800, 2500, 3500, 5000];
  let lastResult = null;
  let lastError = null;

  for (let attempt = 0; attempt <= backoffMs.length; attempt += 1) {
    try {
      const profile = await store.dispatch(getProfile());
      lastResult = profile;

      // Success with backend id/profile should stop retries.
      if (
        (profile?.id && profile?.profileSource !== "token-fallback") ||
        profile?.registrationCompleted === true
      ) {
        return profile;
      }
    } catch (error) {
      lastError = error;
      if (!shouldRetryProfileFetch(error)) {
        throw error;
      }
    }

    if (attempt < backoffMs.length) {
      await delay(backoffMs[attempt]);
    }
  }

  if (lastError && !lastResult) {
    throw lastError;
  }
  return lastResult;
};

const bootstrapAuth = async () => {
  store.dispatch({ type: "SET_AUTH_LOADING", payload: true });

  try {
    const authenticated = await initKeycloak();

    if (authenticated) {
      try {
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
      } catch {}
      // Ensure token is fresh before fetching profile when possible.
      try {
        await refreshToken();
      } catch (tokenError) {
        console.warn("[Auth] Token refresh on bootstrap failed:", tokenError);
      }
      if (authenticated) {
        await fetchProfileWithRetry();
      }
    }

    store.dispatch({
      type: "SET_AUTH",
      payload: {
        isAuthenticated: authenticated,
        keycloak,
        isLoading: false,
      },
    });

    return authenticated;
  } catch (error) {
    console.error("Keycloak bootstrap failed", error);

    store.dispatch({
      type: "SET_AUTH",
      payload: {
        isAuthenticated: false,
        keycloak,
        isLoading: false,
      },
    });

    return false;
  } finally {
    clearKeycloakCallbackParamsFromUrl();
    store.dispatch({ type: "SET_AUTH_LOADING", payload: false });
  }
};

const attachKeycloakEventHandlers = () => {
  keycloak.onAuthSuccess = async () => {
    try {
      sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    } catch {}
    saveStoredKeycloakTokens();
    store.dispatch({
      type: "SET_AUTH",
      payload: {
        isAuthenticated: true,
        keycloak,
        isLoading: false,
      },
    });

    try {
      await fetchProfileWithRetry();
    } catch (profileError) {
      console.error("Profile fetch after auth success failed", profileError);
    }
  };

  keycloak.onAuthError = (err) => {
    console.error("Keycloak auth error", err);
  };

  keycloak.onAuthRefreshSuccess = () => {
    saveStoredKeycloakTokens();
  };

  keycloak.onAuthLogout = () => {
    clearStoredKeycloakTokens();
    store.dispatch({ type: LOGOUT });
    store.dispatch({
      type: "SET_AUTH",
      payload: {
        isAuthenticated: false,
        keycloak,
        isLoading: false,
      },
    });

    try {
      sessionStorage.clear();
    } catch {}
  };

  keycloak.onTokenExpired = async () => {
    try {
      if (!keycloak.authenticated) return;
      const refreshed = await refreshToken();
      if (!refreshed) {
        console.warn("[Auth] Token refresh failed; waiting for next protected request.");
      }
    } catch (refreshError) {
      console.warn("[Auth] Token refresh failed; waiting for next protected request.", refreshError);
    }
  };
};

let hasAttachedReturnRefreshListeners = false;

const attachReturnRefreshListeners = () => {
  if (hasAttachedReturnRefreshListeners || typeof window === "undefined") {
    return;
  }

  hasAttachedReturnRefreshListeners = true;

  const refreshOnReturn = async () => {
    if (document.visibilityState !== "visible") return;
    if (!keycloak.authenticated) return;

    try {
      await refreshToken();
    } catch (error) {
      console.warn("[Auth] Token refresh on tab return failed:", error);
    }
  };

  document.addEventListener("visibilitychange", refreshOnReturn);
  window.addEventListener("focus", refreshOnReturn);
};

attachKeycloakEventHandlers();
attachReturnRefreshListeners();

renderReactApp();
bootstrapAuth().catch(() => {});
